import type {KeyboardEvent, ReactElement} from 'react';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {SuggestionPill} from '@Components/template-submission-flow/components/suggestion-pill';
import {InputField} from '@Components/input-field-v2';
import useOutsideClick from '@Hooks/useOutsideClick';
import {ControlledListItemType3} from '@Components/controlled-list/components/controlled-list-item-type-3';
import {TagInputType, type TagsInputFieldProps} from '@Components/tags-input-field/tags-input-field.types';
import {InputFieldState} from '@Components/input-field-v2/input-field.types';
import {Text, TextSize} from '@Components/text';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {ControlledListItemSize} from '@Components/controlled-list/controlled-list.types';
import {debounce} from '@Utils/misc.util';
import styles from './tags-input-field.module.scss';
import {SuggestionPillType} from '@Components/template-submission-flow/components/suggestion-pill/suggestion-pill.types';
import type {ControlledListItemType2Props} from '@Components/controlled-list/components/controlled-list-item-type-2';
import {ControlledListItemType2} from '@Components/controlled-list/components/controlled-list-item-type-2';
import {isInvalidTagInput} from '@Components/tags-input-field/tags-input-field-library';
import {Popover} from '../popover';

export function TagsInputField({
  allSelectedTags = [],
  allSuggestedTags = [],
  onInputChangeCallback,
  inputClassName = '',
  onSaveTagCallback,
  onPillClick,
  onRemoveTag,
  inputPlaceholder = '',
  doAutofocusOnBlur = true,
  inputFieldState = InputFieldState.DEFAULT,
  informationText = '',
  dropdownClassName = '',
  shouldFlip = false,
  wrapperClassName = '',
  tagsLimit = 5,
  suggestionPillClassName,
  suggestionPillIsBoldText,
  inputContainerClassName = '_position-relative',
  tagInputType = TagInputType.TEXT,
  inputFieldClassName = '',
  inputFieldContainerClassName = '',
  shouldHideDropdownWhenEmpty = false,
  doAutofocusOnMount = false,
  suggestionsPopoverRef = undefined,
  doAutofocusOnSubmit = false,
  maxTagLength = undefined,
  ...props
}: TagsInputFieldProps): ReactElement<any> {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestedTags, setShowSuggestedTags] = useState(false);
  const [currentIndex, setCurrentIndex] = useState<number>(-1);
  const suggestionsListRef = useRef<HTMLUListElement>(null);
  const inputContainerRef = useRef<HTMLDivElement>(null);
  const isUserInteractingWithDropdown = useRef(false);
  const suggestedTagsRef = useRef<HTMLDivElement>(null);
  const tagInputCompletedRef = useRef<boolean>(false);
  const inputFieldRef = useRef<HTMLInputElement>(null);
  const inputClearedRef = useRef<boolean>(false);
  const [isFlipped, setIsFlipped] = useState(false);
  const TAG_INPUT_LENGTH_FACTOR = 11;

  useEffect(() => {
    if (doAutofocusOnMount) {
      inputFieldRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    if (doAutofocusOnSubmit) {
      inputFieldRef.current?.focus();
    }
  }, [allSelectedTags]);

  useEffect(() => {
    scrollToBottom();
  }, [allSelectedTags]);

  const scrollToBottom = (): void => {
    if (inputContainerRef.current) {
      inputContainerRef.current.scrollTop = inputContainerRef.current.scrollHeight;
    }
  };

  const handleScroll = useCallback(
    debounce((): void => {
      if (suggestedTagsRef.current) {
        const list = suggestedTagsRef.current;
        const rect = list.getBoundingClientRect();

        const isOutOfView = rect.bottom + rect.height > window.innerHeight;
        setIsFlipped(isOutOfView);
      }
    }, 100),
    [suggestedTagsRef.current]
  );

  useEffect((): VoidFunction | undefined => {
    if (shouldFlip) {
      window.addEventListener('scroll', handleScroll);
      handleScroll();

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
    return undefined;
  }, [shouldFlip, suggestedTagsRef.current]);

  const resetInputContainer = (forceClear = true): void => {
    const inputParts = inputValue
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);

    const invalidInputs = inputParts.filter((part) => isInvalidTagInput(part, tagInputType));

    if (forceClear || !(props.retainInputOnError && invalidInputs.length > 0)) {
      setInputValue('');
      setShowSuggestedTags(false);
    } else if (props.retainInputOnError && invalidInputs.length > 0) {
      setInputValue(invalidInputs.join(', '));
    }

    setCurrentIndex(-1);
    suggestionsListRef.current?.scrollTo({
      top: 0,
    });
    tagInputCompletedRef.current = true;
  };

  const displaySelectedTags = (): ReactElement<any>[] => {
    const selectedTags = [];
    for (let i = 0; i < allSelectedTags.length; i++) {
      if (selectedTags.length < tagsLimit) {
        selectedTags.push(
          <SuggestionPill
            id={allSelectedTags[i]}
            text={allSelectedTags[i]}
            onClick={onPillClick}
            hasRightIcon="icon-close"
            type={SuggestionPillType.NEUTRAL}
            className={`${suggestionPillClassName} spacing-p-b-1 spacing-p-t-1 spacing-p-r-2 spacing-p-l-2`}
            isTextBold={suggestionPillIsBoldText}
            key={i}
          />
        );
      }
    }
    return selectedTags;
  };

  const isNumTagsLimitReached = (): boolean => {
    return allSelectedTags.length >= tagsLimit;
  };

  const isTagAdded = (): boolean => {
    return allSelectedTags.length > 0;
  };

  const onInputChange = (input: string): void => {
    setInputValue(input);
    inputClearedRef.current = input === '';
    tagInputCompletedRef.current = false;
    onInputChangeCallback(input, tagInputCompletedRef);
    if (!tagInputCompletedRef.current && !isInvalidTagInput(input, tagInputType)) {
      setShowSuggestedTags(true);
    } else {
      setShowSuggestedTags(false);
    }
  };

  const saveTag = (tagInput: string): void => {
    const tags = tagInput.split(',').map((tag) => {
      return tag.trim();
    });
    let remainingTagsToAdd = tagsLimit - allSelectedTags.length;
    for (let i = 0; i < tags.length; i++) {
      const index = allSelectedTags.indexOf(tags[i]);
      if (index === -1 && remainingTagsToAdd > 0 && tags[i]) {
        remainingTagsToAdd -= 1;
        onSaveTagCallback(tags[i]);
      }
    }
  };

  const onBlurInputField = (): void => {
    if (inputValue.length > 0 && !isUserInteractingWithDropdown.current && !tagInputCompletedRef.current) {
      saveTag(inputValue);
      resetInputContainer(false);
    }
    if (doAutofocusOnBlur) {
      inputFieldRef.current?.focus();
    }
  };

  const onKeyUpInputField = (e: KeyboardEvent): void => {
    if (e.key === 'Backspace' && !inputValue && !inputClearedRef.current && allSelectedTags.length > 0) {
      onRemoveTag();
      resetInputContainer();
      return;
    }
    inputClearedRef.current = false;

    if (e.key === ',') {
      onBlurInputField();
    }
  };

  const onSuggestedTagsOutsideClick = (): void => {
    resetInputContainer();
  };

  const selectTag = (tag: string): void => {
    saveTag(tag);
    inputFieldRef.current?.focus();
    resetInputContainer();
  };

  const getSuggestedTagsListItems = (): ReactElement<any>[] => {
    if (props.showPreviewTags && props.previewTagsDetail && props.previewTagsDetail.length > 0) {
      return props.previewTagsDetail.map((data: ControlledListItemType2Props, index: number) => (
        <ControlledListItemType2
          onClick={() => {
            selectTag(data.subText as string);
          }}
          id={data.subText as string}
          key={data.subText as string}
          subText={data.subText}
          text={data.text}
          thumbnail={data.thumbnail}
          className={index === currentIndex ? styles.activeListItem : ''}
          textSize={data.textSize}
        />
      ));
    }

    return allSuggestedTags.map((tag, index) => {
      return (
        <ControlledListItemType3
          id={tag}
          text={tag}
          onClick={selectTag}
          key={tag}
          className={index === currentIndex ? styles.activeListItem : ''}
          listSize={ControlledListItemSize.SMALL}
        />
      );
    });
  };

  useOutsideClick(suggestedTagsRef, onSuggestedTagsOutsideClick, null, () => {
    return showSuggestedTags;
  });

  const onArrowDown = (): void => {
    if (currentIndex < allSuggestedTags.length - 1 && suggestionsListRef.current) {
      setCurrentIndex(currentIndex + 1);
      const currentHeight = 54 * (currentIndex + 2) + 12;
      if (currentHeight > suggestionsListRef.current.offsetHeight) {
        suggestionsListRef.current.scrollTo({
          top: suggestionsListRef.current.scrollTop + 54,
          behavior: 'instant',
        });
      }
    }
  };

  const onArrowUp = (): void => {
    if (currentIndex > 0 && suggestionsListRef.current) {
      setCurrentIndex(currentIndex - 1);
      suggestionsListRef.current.scrollTo({
        top: Math.max(suggestionsListRef.current.scrollTop - 54, 0),
        behavior: 'instant',
      });
    }
  };

  const getCurrentActiveListItem = (): string => {
    return allSuggestedTags[currentIndex];
  };

  const onInputSubmit = (): void => {
    if (currentIndex >= 0) {
      saveTag(getCurrentActiveListItem());
      resetInputContainer();
      inputFieldRef.current?.focus();
    }
  };

  const isInputInErrorState = (): boolean => {
    return inputFieldState === InputFieldState.ERROR;
  };

  const getInformationText = (): ReactElement<any> | undefined => {
    if (informationText) {
      return (
        <div className={`${styles.informationField} flex-row-align-center ${isInputInErrorState() ? 'content-danger' : 'content-sub-text'}`}>
          <Icon icon={isInputInErrorState() ? 'icon-exclamation-triangle' : 'icon-info'} size={IconSize.SIZE_ICON_16} className="spacing-m-r-1" />
          <Text val={informationText ?? ''} size={TextSize.XXSMALL} />
        </div>
      );
    }
    return undefined;
  };

  const getNoMatchFoundMessage = (): ReactElement<any> => {
    return <Text val={window.i18next.t('pmwjs_no_match_found')} className="content-disabled spacing-p-b-2 spacing-p-l-3 spacing-p-t-2 spacing-p-r-3" size={TextSize.XSMALL} />;
  };

  const shouldHideDropdownWhenNoSuggestions = (): boolean => {
    return !((showSuggestedTags && allSuggestedTags.length > 0) || inputValue.length > 0);
  };

  const isDropdownHiddenDueToEmptyState = (): boolean => {
    return shouldHideDropdownWhenEmpty && (inputValue.length === 0 || allSuggestedTags.length === 0);
  };

  const getSuggestedListClasses = (): string => {
    const shouldBeHidden = isInputInErrorState() || shouldHideDropdownWhenNoSuggestions() || isDropdownHiddenDueToEmptyState();
    return `flexbox border-s-standard radius-8 ${!allSuggestedTags.length ? 'spacing-p-b-3' : ''} ${dropdownClassName} ${styles.suggestionsList} ${isFlipped ? styles.flip : ''} ${shouldBeHidden ? '_hidden' : ''}`;
  };

  return (
    <div className={wrapperClassName}>
      <div className={`${styles.inputContainer} ${inputContainerClassName}`} ref={inputContainerRef}>
        <div
          className={`${styles.inputFieldContainer} spacing-p-b-3 spacing-p-l-3 spacing-p-r-1 spacing-p-t-1 radius-4 border-s-darker flex-row-align-center ${inputClassName} ${inputFieldState === InputFieldState.ERROR ? styles.errorState : ''}`}
        >
          {displaySelectedTags()}
          <InputField
            input={inputValue}
            placeholder={inputPlaceholder}
            className={`flex-1 ${styles.inputField} ${isNumTagsLimitReached() ? '_hidden' : ''} ${inputFieldContainerClassName}`}
            onInputChange={onInputChange}
            inputClassName={`_borderless _unpadded spacing-m-t-2 ${isInputInErrorState() ? styles.inputErrorText : ''} ${inputFieldClassName}`}
            onKeyUp={onKeyUpInputField}
            onInputBlur={onBlurInputField}
            ref={inputFieldRef}
            retainFocusOnTab
            isDisabled={isNumTagsLimitReached()}
            onArrowDownKeyDown={onArrowDown}
            onArrowUpKeyDown={onArrowUp}
            onInputSubmit={onInputSubmit}
            useEmptyPlaceholder={isTagAdded()}
            maxLength={maxTagLength ?? TAG_INPUT_LENGTH_FACTOR * tagsLimit}
          />
        </div>
        <Popover
          className={getSuggestedListClasses()}
          referenceElementRef={suggestionsPopoverRef ?? inputContainerRef}
          popoverCloseFunction={() => {
            setShowSuggestedTags(false);
          }}
          isPositionFixed
          shouldInheritReferenceElementWidth
        >
          <ul
            ref={suggestionsListRef}
            className={`_unmargin _unpadded _full-width ${styles.scrollableList}`}
            onMouseEnter={() => {
              isUserInteractingWithDropdown.current = true;
            }}
            onMouseLeave={() => {
              isUserInteractingWithDropdown.current = false;
            }}
          >
            {allSuggestedTags.length ? getSuggestedTagsListItems() : getNoMatchFoundMessage()}
          </ul>
        </Popover>
      </div>
      {getInformationText()}
    </div>
  );
}
